import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  MagnifyingGlass, Star, MapPin, Clock, Storefront,
  Coffee, ForkKnife, Scissors, ShoppingBag, Flower,
  ArrowRight, X, Crown, List, MapTrifold
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lazy load the map component
const MerchantMapComponent = lazy(() => import('../../components/MerchantMap'));

// Loading placeholder for map
const MapLoadingPlaceholder = () => (
  <div className="relative w-full h-[500px] bg-[#1E2F26] rounded-2xl overflow-hidden flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-[#8BA89A] text-sm">Chargement de la carte...</p>
    </div>
  </div>
);

const Network = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  const categories = [
    { id: 'all', label: t('network_cat_all'), icon: Storefront },
    { id: 'café', label: t('network_cat_cafe'), icon: Coffee },
    { id: 'restaurant', label: t('network_cat_restaurant'), icon: ForkKnife },
    { id: 'boutique', label: t('network_cat_boutique'), icon: ShoppingBag },
    { id: 'salon', label: t('network_cat_salon'), icon: Scissors },
    { id: 'fleuriste', label: t('network_cat_florist'), icon: Flower }
  ];

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/network/merchants`);
      setMerchants(data.merchants || []);
    } catch (error) {
      console.error('Failed to fetch merchants:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMerchants = merchants.filter(m => {
    const matchesSearch = m.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         m.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || 
                           m.category?.toLowerCase() === activeCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category?.toLowerCase());
    return cat?.icon || Storefront;
  };

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('network_badge')}
            </p>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {merchants.length}{t('network_title_suffix')}
            </h1>
            <p className="text-[#8BA89A] max-w-xl mx-auto">
              {t('network_subtitle')}
            </p>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-xl mx-auto mb-8"
          >
            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A]" />
              <input
                type="text"
                placeholder={t('network_search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-[#1E2F26] border border-[#283D32] rounded-xl pl-12 pr-4 py-4 text-white placeholder:text-[#8BA89A] focus:outline-none focus:border-[#C3A365]/50 transition-colors"
                data-testid="network-search-input"
              />
            </div>
          </motion.div>

          {/* Categories */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-2 mb-6"
          >
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all ${
                    isActive
                      ? 'bg-[#C3A365] text-[#0F1A14]'
                      : 'bg-[#1E2F26] text-[#8BA89A] hover:text-white hover:bg-[#283D32]'
                  }`}
                  data-testid={`network-cat-${cat.id}`}
                >
                  <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                  <span className="text-sm font-medium">{cat.label}</span>
                </button>
              );
            })}
          </motion.div>

          {/* View Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex justify-center mb-8"
          >
            <div className="flex bg-[#1E2F26] p-1 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  viewMode === 'list' ? 'bg-[#C3A365] text-[#0F1A14]' : 'text-[#8BA89A]'
                }`}
                data-testid="view-list-btn"
              >
                <List size={18} />
                <span className="text-sm">{t('network_view_list') || 'Liste'}</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  viewMode === 'map' ? 'bg-[#C3A365] text-[#0F1A14]' : 'text-[#8BA89A]'
                }`}
                data-testid="view-map-btn"
              >
                <MapTrifold size={18} />
                <span className="text-sm">{t('network_view_map') || 'Carte'}</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Merchants Content */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : viewMode === 'map' ? (
            <Suspense fallback={<MapLoadingPlaceholder />}>
              <MerchantMapComponent 
                merchants={filteredMerchants} 
                onSelectMerchant={setSelectedMerchant}
                language={language}
              />
            </Suspense>
          ) : filteredMerchants.length === 0 ? (
            <div className="text-center py-20">
              <Storefront size={48} className="text-[#8BA89A] mx-auto mb-4" weight="thin" />
              <p className="text-[#8BA89A]">{t('network_no_results')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMerchants.map((merchant, i) => {
                const CategoryIcon = getCategoryIcon(merchant.category);
                return (
                  <motion.div
                    key={merchant.id || i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => setSelectedMerchant(merchant)}
                    className="group cursor-pointer bg-[#1E2F26]/50 border border-[#283D32] hover:border-[#C3A365]/30 transition-all overflow-hidden"
                    data-testid={`merchant-card-${i}`}
                  >
                    {/* Image placeholder */}
                    <div className="h-40 bg-gradient-to-br from-[#145A3A] to-[#0F1A14] flex items-center justify-center relative overflow-hidden">
                      <CategoryIcon size={48} className="text-[#C3A365]/30" weight="thin" />
                      {merchant.featured && (
                        <div className="absolute top-3 right-3 px-2 py-1 bg-[#C3A365] text-[#0F1A14] text-xs font-bold rounded">
                          {t('network_featured')}
                        </div>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-medium text-white group-hover:text-[#C3A365] transition-colors">
                            {merchant.business_name}
                          </h3>
                          <p className="text-[#C3A365] text-sm">{merchant.category}</p>
                        </div>
                        {merchant.rating && (
                          <div className="flex items-center gap-1 bg-[#283D32] px-2 py-1 rounded">
                            <Star size={12} className="text-[#C3A365]" weight="fill" />
                            <span className="text-[#C3A365] text-sm font-medium">{merchant.rating}</span>
                          </div>
                        )}
                      </div>

                      {merchant.description && (
                        <p className="text-[#8BA89A] text-sm mb-3 line-clamp-2">{merchant.description}</p>
                      )}

                      {merchant.address && (
                        <div className="flex items-center gap-2 text-[#8BA89A] text-sm">
                          <MapPin size={14} />
                          <span className="truncate">{merchant.address}</span>
                        </div>
                      )}

                      {merchant.reward_description && (
                        <div className="mt-3 pt-3 border-t border-[#283D32]">
                          <div className="flex items-center gap-2">
                            <Crown size={14} className="text-[#C3A365]" />
                            <span className="text-[#C3A365] text-xs">{merchant.reward_description}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Merchant Detail Modal */}
      <AnimatePresence>
        {selectedMerchant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedMerchant(null)}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10001] flex items-end sm:items-center justify-center"
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#18261E] rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="relative h-48 bg-gradient-to-br from-[#145A3A] to-[#0F1A14] flex items-center justify-center">
                {(() => {
                  const CategoryIcon = getCategoryIcon(selectedMerchant.category);
                  return <CategoryIcon size={64} className="text-[#C3A365]/30" weight="thin" />;
                })()}
                <button
                  onClick={() => setSelectedMerchant(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  data-testid="merchant-modal-close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-medium text-white">{selectedMerchant.business_name}</h2>
                    <p className="text-[#C3A365]">{selectedMerchant.category}</p>
                  </div>
                  {selectedMerchant.rating && (
                    <div className="flex items-center gap-1 bg-[#283D32] px-3 py-1.5 rounded">
                      <Star size={14} className="text-[#C3A365]" weight="fill" />
                      <span className="text-[#C3A365] font-medium">{selectedMerchant.rating}</span>
                    </div>
                  )}
                </div>

                {selectedMerchant.description && (
                  <p className="text-[#8BA89A] mb-6 leading-relaxed">{selectedMerchant.description}</p>
                )}

                {/* Info */}
                <div className="space-y-3 mb-6">
                  {selectedMerchant.address && (
                    <div className="flex items-center gap-3 text-[#8BA89A]">
                      <MapPin size={18} className="text-[#C3A365]" />
                      <span>{selectedMerchant.address}</span>
                    </div>
                  )}
                  {selectedMerchant.hours && (
                    <div className="flex items-center gap-3 text-[#8BA89A]">
                      <Clock size={18} className="text-[#C3A365]" />
                      <span>{selectedMerchant.hours}</span>
                    </div>
                  )}
                </div>

                {/* Reward */}
                {selectedMerchant.reward_description && (
                  <div className="p-4 bg-[#C3A365]/10 border border-[#C3A365]/30 rounded-xl mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown size={18} className="text-[#C3A365]" />
                      <span className="text-[#C3A365] font-medium">{t('network_loyalty_reward')}</span>
                    </div>
                    <p className="text-white text-sm">{selectedMerchant.reward_description}</p>
                  </div>
                )}

                {/* CTA */}
                <button
                  onClick={() => navigate('/activer')}
                  className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold flex items-center justify-center gap-2 hover:bg-[#D4B576] transition-colors"
                  data-testid="merchant-modal-cta"
                >
                  {t('network_join_cta')}
                  <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('network_merchant_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('network_merchant_cta_desc')}
          </p>
          <motion.button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#C3A365] text-[#C3A365] font-semibold hover:bg-[#C3A365] hover:text-[#0F1A14] transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="network-merchant-cta"
          >
            {t('network_merchant_cta_btn')} <ArrowRight size={18} />
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Network;
